import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Compass, X, Home } from "lucide-react";
import api from "../../lib/api";
import { openOnboarding } from "../onboarding/EmptyState";
import { Primary } from "./ui";

export const guidesChanged = () => window.dispatchEvent(new Event("guides-changed"));

// Coquille commune : écran d'intro + aperçu, puis étapes du module, puis écran de succès (géré par Steps).
export const ModuleGuide = ({ module, label, intro, benefit, preview, Steps, autoOpen = true }) => {
  const [params, setParams] = useSearchParams();
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("intro");

  useEffect(() => {
    api.get("/guides/status").then((r) => {
      setSt(r.data);
      const m = r.data.modules[module] || {};
      if (module === "bank" && params.get("powens") === "connected" && m.status === "started") { setPhase("steps"); setOpen(true); return; }
      if (params.get("guide") === module || (autoOpen && r.data.has_property && !m.status && !m.done)) setOpen(true);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module]);

  const clearParam = () => { if (params.get("guide")) { params.delete("guide"); setParams(params, { replace: true }); } };
  const close = (status) => {
    if (status) api.put(`/guides/${module}/status`, { status }).catch(() => {});
    setOpen(false); setPhase("intro"); clearParam(); guidesChanged();
  };
  const start = () => { api.put(`/guides/${module}/status`, { status: "started" }).catch(() => {}); setPhase("steps"); };

  return (
    <>
      <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-soft bg-white hover:bg-brand-softer text-sm font-medium text-brand-greenDeep" data-testid={`guide-open-${module}`}>
        <Compass className="w-4 h-4 text-brand-limeDark" /> {label}
      </button>
      {open && st && (
        <div className="fixed inset-0 z-[70] bg-brand-greenDeep/40 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" data-testid={`guide-${module}`}>
          <div className="bg-white w-full sm:max-w-2xl h-[100dvh] sm:h-auto sm:max-h-[92vh] overflow-y-auto sm:rounded-3xl px-6 sm:px-8 pt-6 animate-in fade-in slide-in-from-bottom-4">
            <div className="flex justify-end"><button onClick={() => close(phase === "intro" && !(st.modules[module] || {}).status ? "dismissed" : null)} className="text-brand-green/40 hover:text-brand-greenDeep" data-testid="guide-close"><X className="w-5 h-5" /></button></div>
            {!st.has_property ? (
              <div className="text-center py-10 pb-12" data-testid="guide-need-property">
                <Home className="w-10 h-10 mx-auto text-brand-limeDark mb-4" />
                <p className="text-lg font-medium text-brand-greenDeep">Créez d'abord votre bien.</p>
                <p className="text-sm text-brand-green/60 mt-1">Ce module s'appuie sur vos biens pour calculer vos résultats.</p>
                <Primary className="mt-6" onClick={() => { close(null); openOnboarding(1); }} data-testid="guide-create-property">Créer mon bien</Primary>
              </div>
            ) : phase === "intro" ? (
              <div className="pb-8" data-testid="guide-intro">
                <p className="text-xs uppercase tracking-[0.2em] text-brand-limeDark font-semibold">{label}</p>
                <h2 className="font-display text-3xl sm:text-4xl text-brand-greenDeep mt-2 leading-tight">{intro}</h2>
                <p className="text-brand-green/70 mt-3">{benefit}</p>
                {preview && <div className="mt-6 rounded-2xl border border-dashed border-brand-soft p-5 relative"><span className="absolute -top-2.5 left-4 px-2 bg-white text-[11px] font-semibold text-amber-700">Exemple</span>{preview}</div>}
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Primary onClick={start} data-testid="guide-start">C'est parti</Primary>
                  <button onClick={() => close("dismissed")} className="text-sm text-brand-green/60 hover:text-brand-greenDeep underline underline-offset-4" data-testid="guide-later">Plus tard</button>
                </div>
              </div>
            ) : (
              <Steps properties={st.properties} onDone={() => close("completed")} />
            )}
          </div>
        </div>
      )}
    </>
  );
};
